import { Controller } from "@hotwired/stimulus";

// Gap between the panel's bottom edge and the viewport (px)
const VIEWPORT_MARGIN = 16;
// Must match the panel's transition duration (duration-150)
const TRANSITION_MS = 150;

// Set when a mouse click on a trigger navigates to its page (/countries, /travel). The cursor is
// still over that trigger on the new page, and hover would open the panel on top of it. Module
// scope survives Turbo navigations; the menu is kept by its panel id (the trigger's
// aria-controls), because Turbo replaces the trigger element itself. Cleared once the pointer
// is over anything else, the other menu's trigger included.
let suppressedMenu: string | null = null;

const clearHoverSuppression = (event: PointerEvent) => {
  if (suppressedMenu && (event.target as Element).closest?.(`[aria-controls="${suppressedMenu}"]`)) return;

  suppressedMenu = null;
  document.removeEventListener("pointerover", clearHoverSuppression);
};

const suppressHoverUntilPointerMovesAway = (menu: string | null) => {
  suppressedMenu = menu;
  document.addEventListener("pointerover", clearHoverSuppression);
};

/**
 * Menus in the desktop navbar (lg+): Countries (this identifier, mega-menu) and Travel
 * (travel-menu, a re-export of this class). Lives on nav#main-navbar; each panel is a sibling
 * of .navbar so its backdrop-filter does not become the panel's containing block.
 *
 * Trigger (the "Countries" or "Travel" link):
 * - mouse: hover opens the panel, click follows the link (/countries, /travel)
 * - touch/pen: tap toggles the panel (there is no hover to preview it)
 * - keyboard: Enter follows the link, ArrowDown/Space opens the panel and focuses the
 *   selected continent; Tab from an open trigger moves into the panel
 *
 * Panel: continents are vertical tabs (arrow keys, Home/End, hover with a short intent
 * delay), Escape and click outside close it, Tab past the last link returns focus to
 * the navbar item after the trigger.
 *
 * Turbo caches a snapshot of the page before leaving it; `reset` closes the panel first,
 * otherwise Back would restore the page with the menu open.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["trigger", "panel", "tab", "tabPanel"];

  static values = {
    openDelay: { type: Number, default: 80 },
    closeDelay: { type: Number, default: 200 },
    tabDelay: { type: Number, default: 100 },
  };

  declare readonly triggerTarget: HTMLElement;
  declare readonly panelTarget: HTMLElement;
  declare readonly tabTargets: HTMLButtonElement[];
  declare readonly tabPanelTargets: HTMLElement[];
  declare openDelayValue: number;
  declare closeDelayValue: number;
  declare tabDelayValue: number;

  private isOpen = false;
  private lastPointerType: string | null = null;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private tabTimer: ReturnType<typeof setTimeout> | null = null;
  private flagsWarmed = false;

  private readonly handleDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;

    const focusWasInside = this.element.contains(document.activeElement);
    this.close();
    if (focusWasInside) this.triggerTarget.focus();
  };

  private readonly handleDocumentClick = (event: MouseEvent) => {
    if (!this.element.contains(event.target as Node)) this.close();
  };

  // The source is the controller, not the element: other dropdowns may share nav#main-navbar
  private readonly handleCloseOthers = (event: Event) => {
    if ((event as CustomEvent).detail?.source !== this) this.close();
  };

  connect() {
    window.addEventListener("dropdown:close-others", this.handleCloseOthers);
    this.reset();
  }

  disconnect() {
    this.reset();
    window.removeEventListener("dropdown:close-others", this.handleCloseOthers);
  }

  // --- Trigger -------------------------------------------------------------

  trackPointer(event: PointerEvent) {
    this.lastPointerType = event.pointerType;
    this.warmFlags();
  }

  triggerClick(event: MouseEvent) {
    const pointerType = this.lastPointerType;
    this.lastPointerType = null;

    // Mouse and keyboard (Enter) follow the link; touch has no hover, so a tap toggles.
    // A trigger without a link (Explore) toggles for every kind of click.
    const isLink = this.triggerTarget instanceof HTMLAnchorElement && this.triggerTarget.hasAttribute("href");
    if (!isLink && pointerType === "mouse") {
      // Hover has usually opened it already - a click must not close it again
      event.preventDefault();
      this.show();
      return;
    }
    if (isLink && pointerType !== "touch" && pointerType !== "pen") {
      const opensNewTab = event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0;
      if (pointerType === "mouse" && !opensNewTab) {
        suppressHoverUntilPointerMovesAway(this.triggerTarget.getAttribute("aria-controls"));
        this.close();
      }
      return;
    }

    event.preventDefault();
    if (this.isOpen) {
      this.close();
    } else {
      this.show();
    }
  }

  // Keyboard focus on the trigger: the panel may open next (ArrowDown), so start the flags now
  warm() {
    this.warmFlags();
  }

  triggerKeydown(event: KeyboardEvent) {
    this.warmFlags();
    if (event.key === "ArrowDown" || event.key === " ") {
      event.preventDefault();
      this.show();
      this.focusIntoPanel();
    } else if (event.key === "Tab" && !event.shiftKey && this.isOpen) {
      // The panel comes after the whole navbar in DOM order; jump into it instead
      event.preventDefault();
      this.focusIntoPanel();
    }
  }

  // --- Hover (mouse only; touch fires pointerenter too, but taps are handled on click) ---

  pointerEnter(event: PointerEvent) {
    if (event.pointerType !== "mouse") return;

    this.warmFlags();
    this.clearTimer("closeTimer");
    if (this.isOpen || this.openTimer || this.hoverSuppressed()) return;

    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.show();
    }, this.openDelayValue);
  }

  pointerLeave(event: PointerEvent) {
    if (event.pointerType !== "mouse") return;

    this.clearTimer("openTimer");
    if (!this.isOpen) return;

    this.clearTimer("closeTimer");
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.close();
    }, this.closeDelayValue);
  }

  // --- Panel ---------------------------------------------------------------

  panelKeydown(event: KeyboardEvent) {
    if (event.key !== "Tab") return;

    const focusables = this.panelFocusables();
    if (focusables.length === 0) return;

    if (event.shiftKey && document.activeElement === focusables[0]) {
      event.preventDefault();
      this.triggerTarget.focus();
    } else if (!event.shiftKey && document.activeElement === focusables[focusables.length - 1]) {
      event.preventDefault();
      this.close();
      this.focusAfterTrigger();
    }
  }

  reposition() {
    if (!this.isOpen) return;

    // The trigger is hidden below lg (the mobile drawer has its own Countries link)
    if (this.triggerTarget.offsetParent === null) {
      this.reset();
      return;
    }

    const top = this.panelTarget.getBoundingClientRect().top;
    const maxHeight = window.innerHeight - top - VIEWPORT_MARGIN;
    this.panelTarget.style.maxHeight = `${Math.max(maxHeight, 0)}px`;
  }

  // --- Continent tabs ------------------------------------------------------

  selectTab(event: Event) {
    this.clearTimer("tabTimer");
    this.activateTab(this.tabTargets.indexOf(event.currentTarget as HTMLButtonElement));
  }

  tabPointerEnter(event: PointerEvent) {
    if (event.pointerType !== "mouse") return;

    // Short intent delay so a diagonal sweep across tabs doesn't flip continents
    const index = this.tabTargets.indexOf(event.currentTarget as HTMLButtonElement);
    this.clearTimer("tabTimer");
    this.tabTimer = setTimeout(() => {
      this.tabTimer = null;
      this.activateTab(index);
    }, this.tabDelayValue);
  }

  tabPointerLeave() {
    this.clearTimer("tabTimer");
  }

  tabKeydown(event: KeyboardEvent) {
    const current = this.tabTargets.indexOf(event.currentTarget as HTMLButtonElement);
    const last = this.tabTargets.length - 1;
    let next: number;

    switch (event.key) {
      case "ArrowDown":
        next = current === last ? 0 : current + 1;
        break;
      case "ArrowUp":
        next = current === 0 ? last : current - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activateTab(next);
    this.tabTargets[next].focus();
  }

  // --- Open / close --------------------------------------------------------

  // The ~200 flags in the panel are lazy, so a closed menu costs nothing on page load. A lazy image
  // in a hidden panel only starts loading once it's shown, which left the flags blank for a moment
  // after opening. Switch them to eager as soon as the pointer or the keyboard reaches the trigger,
  // so they load during the open delay instead (once per page)
  private warmFlags() {
    if (this.flagsWarmed) return;

    this.flagsWarmed = true;
    this.panelTarget.querySelectorAll<HTMLImageElement>('img[loading="lazy"]').forEach((img) => {
      img.loading = "eager";
    });
  }

  private show() {
    this.warmFlags();
    this.clearTimer("openTimer");
    this.clearTimer("closeTimer");
    if (this.isOpen) return;

    window.dispatchEvent(new CustomEvent("dropdown:close-others", { detail: { source: this } }));

    this.isOpen = true;
    this.clearTimer("hideTimer");
    const panel = this.panelTarget;
    panel.classList.remove("hidden");
    this.reposition();
    // Force a style flush so the opacity/translate transition runs from the hidden state
    void panel.offsetHeight;
    panel.classList.remove("opacity-0", "-translate-y-1");
    this.triggerTarget.setAttribute("aria-expanded", "true");

    document.addEventListener("keydown", this.handleDocumentKeydown);
    document.addEventListener("click", this.handleDocumentClick);
  }

  close() {
    this.clearTimer("openTimer");
    this.clearTimer("closeTimer");
    this.clearTimer("tabTimer");
    if (!this.isOpen) return;

    this.isOpen = false;
    this.panelTarget.classList.add("opacity-0", "-translate-y-1");
    this.triggerTarget.setAttribute("aria-expanded", "false");
    this.removeDocumentListeners();

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.panelTarget.classList.add("hidden");
    }, TRANSITION_MS);
  }

  // Close immediately, without the transition (Turbo snapshot, disconnect, resize below lg)
  reset() {
    this.clearTimer("openTimer");
    this.clearTimer("closeTimer");
    this.clearTimer("hideTimer");
    this.clearTimer("tabTimer");
    this.isOpen = false;
    this.lastPointerType = null;
    this.panelTarget.classList.add("hidden", "opacity-0", "-translate-y-1");
    this.triggerTarget.setAttribute("aria-expanded", "false");
    this.removeDocumentListeners();
  }

  // --- Helpers -------------------------------------------------------------

  private activateTab(index: number) {
    if (index < 0) return;

    this.tabTargets.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    this.tabPanelTargets.forEach((tabPanel, i) => {
      tabPanel.classList.toggle("invisible", i !== index);
    });
  }

  // The selected tab when the panel has tabs (Countries), otherwise its first link (Explore)
  private focusIntoPanel() {
    (this.selectedTab() ?? this.panelFocusables()[0])?.focus();
  }

  private selectedTab(): HTMLButtonElement | undefined {
    return this.tabTargets.find((tab) => tab.getAttribute("aria-selected") === "true");
  }

  // Focusable elements of the panel in tab order (inactive tabs and hidden lists excluded)
  private panelFocusables(): HTMLElement[] {
    return Array.from(this.panelTarget.querySelectorAll<HTMLElement>("a[href], button")).filter(
      (el) => el.tabIndex >= 0 && el.checkVisibility({ visibilityProperty: true })
    );
  }

  // This menu's trigger was just clicked and the pointer hasn't left it (see suppressedMenu)
  private hoverSuppressed() {
    return suppressedMenu !== null && suppressedMenu === this.triggerTarget.getAttribute("aria-controls");
  }

  // Next visible navbar link (About is hidden between lg and xl)
  private focusAfterTrigger() {
    let item = this.triggerTarget.closest("li")?.nextElementSibling;
    while (item) {
      const link = item.querySelector<HTMLElement>("a[href], button");
      if (link && link.offsetParent !== null) {
        link.focus();
        return;
      }
      item = item.nextElementSibling;
    }
    this.triggerTarget.focus();
  }

  private removeDocumentListeners() {
    document.removeEventListener("keydown", this.handleDocumentKeydown);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  private clearTimer(name: "openTimer" | "closeTimer" | "hideTimer" | "tabTimer") {
    const timer = this[name];
    if (timer) {
      clearTimeout(timer);
      this[name] = null;
    }
  }
}
